import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { BankAccountDto } from 'app/api/apiClient/generated/types';
import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { useGetBankAccountStatuses } from 'app/api/hooks/core/v1/payee/bankAccounts';
import { bankAccountPendingStatusIds, BankAccountStatusId } from 'app/constants/bank-accounts';
import { useInterval } from 'app/hooks/useInterval';
import i18n from 'app/services/i18n';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { AlertLevel } from 'app/state/AlertsContext/Provider';
import { indexBy } from 'app/utils/lib/array';

const getPendingBankStatusIds = (bankAccounts: BankAccountDto[]) =>
  bankAccounts.reduce((acc, bankAccount): string[] =>
    bankAccountPendingStatusIds[bankAccount.status.id as BankAccountStatusId]
      ? [...acc, bankAccount.id]
      : acc,
  []);

interface StatusConfig {
  alertLevel: AlertLevel;
  getMessage: (accountName: string) => string;
  // this needs to be a function to support i18n lazy loading
  getTitle: () => string;
}

const statusConfig: Partial<Record<BankAccountStatusId, StatusConfig>> = {
  [BankAccountStatusId.Active]: {
    alertLevel: 'success',
    // eslint-disable-next-line @stylistic/max-len
    getMessage: (accountName: string) => i18n.t('payee:payeeWallet.approvedNotification.message', 'Account {{accountName}} has been approved and is ready to receive funds.', { accountName }),
    getTitle: () => i18n.t('payee:payeeWallet.approvedNotification.title', 'Account has been approved'),
  },
  [BankAccountStatusId.Rejected]: {
    alertLevel: 'error',
    // eslint-disable-next-line @stylistic/max-len
    getMessage: (accountName: string) => i18n.t('payee:payeeWallet.rejectedNotification.message', 'Account {{accountName}} has been declined.', { accountName }),
    getTitle: () => i18n.t('payee:payeeWallet.rejectedNotification.title', 'Account has been declined'),
  },
};

const additionalVerificationConfig: StatusConfig = {
  alertLevel: 'info',
  getMessage: (accountName: string) =>
    // eslint-disable-next-line @stylistic/max-len
    i18n.t('payee:payeeWallet.additionalVerificationNotification.message', 'Verification is required for account {{accountName}}. We\'ll call you within 7 business days to complete it.', { accountName }),
  // eslint-disable-next-line @stylistic/max-len
  getTitle: () => i18n.t('payee:payeeWallet.additionalVerificationNotification.title', 'Account requires additional verification'),
};

const intervalInMs = 60000; // 1 minute
const maxRetries = 3;

const PayeeBankAccountStatusCheck: FC<PropsWithChildren> = props => {
  const alertsDispatch = useAlertsDispatch();

  const [bankAccountIds, setBankAccountIds] = useState<string[]>([]);
  const [counter, setCounter] = useState(0);

  const user = useGetUser();

  const {
    mutate: getBankAccountStatuses,
  } = useGetBankAccountStatuses();

  const hasStatus = bankAccountIds.length === 0 || counter >= maxRetries;

  const checkStatus = useCallback(() => {
    if (hasStatus) {
      return;
    }

    getBankAccountStatuses(bankAccountIds, {
      onSettled: () => {
        setCounter(prev => prev + 1);
      },
      onSuccess: data => {
        const dataLookup = indexBy(data, bankAccount => bankAccount.id);

        let newBankAccountIds = [...bankAccountIds];

        for (const bankAccountId of bankAccountIds) {
          const statusData = dataLookup[bankAccountId];
          if (!statusData) {
            continue;
          }

          const config = statusData?.isValidationRequired
            ? additionalVerificationConfig
            : statusConfig[statusData.status.id as BankAccountStatusId];

          if (config) {
            alertsDispatch({
              payload: {
                alert: {
                  alertLevel: config.alertLevel,
                  message: config.getMessage(statusData.destinationDescription),
                  shouldAutoHide: false,
                  title: config.getTitle(),
                },
              },
              type: 'addAlert',
            });

            newBankAccountIds = newBankAccountIds.filter(id => id !== bankAccountId);
          }
        }

        // if newBankAccountIds here is the same as above,
        // React will skip this update
        setBankAccountIds(newBankAccountIds);
      },
    });
  }, [alertsDispatch, bankAccountIds, getBankAccountStatuses, hasStatus]);

  useInterval(intervalInMs, checkStatus, !hasStatus);

  useEffect(() => {
    setBankAccountIds(getPendingBankStatusIds(user?.company?.bankAccounts ?? []));
    setCounter(0);
  }, [user?.company?.bankAccounts]);

  return props.children;
};

export default PayeeBankAccountStatusCheck;
